import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Stack, Typography, Divider, Chip} from 'views/components';
import {TaskEditor, Translate} from 'views/module';

import TaskView from './TaskView';

const TaskList = (props) => {
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);
  const [editingTaskRn, setEditingTaskRn] = React.useState(null);
  const [activeFilter, setActiveFilter] = React.useState('all');

  const caregiverTasks = props.caregiverTasks;
  const caregiverBackend = props.caregiverBackend;
  const caregiverData = props.caregiverData;
  const playbooks = props.playbooks;
  const [caregiverFirstName, ...caregiverLastNameParts] = caregiverData.caregiverName.split(' ');
  const caregiverLastName = caregiverLastNameParts.join(' ');

  const groupedTasks = React.useMemo(() => {
    const currentDate = new Date();
    const now = Date.now();
    const today = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()).getTime();

    return caregiverTasks.reduce((result, task) => {
      if (task.status === 'COMPLETE') {
        result.completed.push(task);
        return result;
      }

      if (!task.dueDate) {
        result.dueLater.push(task);
        return result;
      }

      const dueDate = new Date(task.dueDate);
      const dueTime = dueDate.getTime();
      const dueDay = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate()).getTime();

      if (dueTime < now) {
        result.overdue.push(task);
      } else if (dueDay === today) {
        result.dueToday.push(task);
      } else {
        result.dueLater.push(task);
      }

      return result;
    }, {
      overdue: [],
      dueToday: [],
      dueLater: [],
      completed: [],
    });
  }, [caregiverTasks]);

  const sections = [
    {
      key: 'overdue',
      title: 'caregiver.CaregiverDetail.Tasks.Sections.Overdue',
      color: 'error.main',
      tasks: groupedTasks.overdue,
    },
    {
      key: 'dueToday',
      title: 'caregiver.CaregiverDetail.Tasks.Sections.DueToday',
      color: '#6B6760',
      tasks: groupedTasks.dueToday,
    },
    {
      key: 'dueLater',
      title: 'caregiver.CaregiverDetail.Tasks.Sections.DueLater',
      color: '#6B6760',
      tasks: groupedTasks.dueLater,
    },
    {
      key: 'completed',
      title: 'caregiver.CaregiverDetail.Tasks.Sections.Completed',
      color: '#6B6760',
      tasks: groupedTasks.completed,
    },
  ].filter((section) => section.tasks.length > 0);

  const filters = React.useMemo(() => {
    const baseFilters = [
      {key: 'all', title: 'caregiver.CaregiverDetail.Tasks.Filters.All', isTranslated: true},
      {key: 'overdue', title: 'caregiver.CaregiverDetail.Tasks.Sections.Overdue', isTranslated: true},
      {key: 'dueToday', title: 'caregiver.CaregiverDetail.Tasks.Sections.DueToday', isTranslated: true},
      {key: 'dueLater', title: 'caregiver.CaregiverDetail.Tasks.Sections.DueLater', isTranslated: true},
      {key: 'completed', title: 'caregiver.CaregiverDetail.Tasks.Sections.Completed', isTranslated: true},
    ];
    const seenPlaybooks = new Set();

    caregiverTasks.forEach((task) => {
      if (!/^RN:PLAYBOOK-V1-[^:]+$/.test(task.playbookRn) || seenPlaybooks.has(task.playbookRn)) {
        return;
      }

      const playbook = playbooks.find((currentPlaybook) => currentPlaybook.playbookRn === task.playbookRn);

      if (!playbook) {
        return;
      }

      seenPlaybooks.add(task.playbookRn);
      baseFilters.push({
        key: task.playbookRn,
        title: `/${playbook.title}`,
        isTranslated: false,
      });
    });

    return baseFilters;
  }, [caregiverTasks, playbooks]);

  const visibleSections = React.useMemo(() => {
    if (activeFilter === 'all') {
      return sections;
    }

    if (activeFilter.startsWith('RN:PLAYBOOK-V1-')) {
      return sections
          .map((section) => ({
            ...section,
            tasks: section.tasks.filter((task) => activeFilter === task.playbookRn),
          }))
          .filter((section) => section.tasks.length > 0);
    }

    return sections.filter((section) => section.key === activeFilter);
  }, [activeFilter, sections, playbooks]);

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditingTaskRn(null);
  };

  const addTask = () => {
    setEditingTaskRn(null);
    setIsEditorOpen(true);
  };

  const editTask = (task) => {
    setIsEditorOpen(false);
    setEditingTaskRn(task.taskRn);
  };

  const edits = {
    addTask: (taskData) => {
      const complete = () => {
        setIsEditorOpen(false);
      };
      caregiverBackend.tasks.addTask(taskData, complete);
    },
    setTaskStatusToComplete: (taskRn, isComplete) => {
      if (isComplete) {
        return;
      }

      caregiverBackend.tasks.updateTaskStatus(taskRn, 'COMPLETE');
    },
    editTask: (taskRn, taskData) => {
      const complete = () => {
        setEditingTaskRn(null);
      };
      caregiverBackend.tasks.updateTask(taskRn, taskData, complete);
    },
    removeTask: (taskData) => {
      const complete = () => {
        setEditingTaskRn((previousTaskRn) => (previousTaskRn === taskData.taskRn ? null : previousTaskRn));
      };
      caregiverBackend.tasks.removeTask(taskData.taskRn, complete);
    },
  };

  const renderTask = (task, index) => {
    return editingTaskRn === task.taskRn ?
      <Box key={task.taskRn || index} sx={{mb: 1, px: 2, pt: 1}}>
        <TaskEditor
          isEditing
          task={task}
          onCancel={closeEditor}
          onSubmit={(payload) => edits.editTask(task.taskRn, payload)}
        />
      </Box> :
      <TaskView
        key={task.taskRn || index}
        task={task}
        handleSetStatusToComplete={edits.setTaskStatusToComplete}
        onClickEditTask={editTask}
        removeTask={edits.removeTask}
      />;
  };

  return (
    <Stack>
      <Stack
        direction='row'
        gap={1}
        sx={{
          px: 2,
          pt: 2,
          pb: sections.length > 0 ? 1 : 0,
          flexWrap: 'wrap',
        }}
      >
        {
          filters.map((filter) => {
            const isActive = activeFilter === filter.key;

            return (
              <Chip
                key={filter.key}
                component='button'
                size='small'
                onClick={() => setActiveFilter(filter.key)}
                label={filter.isTranslated ? <Translate expression={filter.title} /> : filter.title}
                sx={{
                  backgroundColor: isActive ? '#FFF7F4' : '#F7F4EF',
                  border: '1px solid',
                  borderColor: isActive ? '#EBBDAD' : '#E8E4DE',
                  fontFamily: '"Roboto Mono", monospace',
                  color: isActive ? '#DA7955' : '#6B6760',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              />
            );
          })
        }
      </Stack>
      {
        visibleSections.map((section, index) => (
          <React.Fragment key={section.key}>
            {
              (isEditorOpen && section.key === 'completed') &&
                <Box sx={{mt: 1, px: 2}}>
                  <TaskEditor
                    onCancel={closeEditor}
                    onSubmit={edits.addTask}
                    task={{
                      connectionInfo: [{
                        connectionRn: caregiverData.caregiverRn,
                        firstName: caregiverFirstName,
                        lastName: caregiverLastName,
                      }],
                    }}
                  />
                </Box>
            }
            {
              (!isEditorOpen && section.key === 'completed') &&
                <Box sx={{px: 2, py: 1}}>
                  <Box
                    component='button'
                    onClick={addTask}
                    sx={{
                      background: 'none',
                      border: 'none',
                      color: 'primary.main',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '14px',
                      p: 0,
                    }}
                  >
                    <Box component='span'>+</Box>
                    <Translate expression='tasks.Common.NewTask' />
                  </Box>
                </Box>
            }
            {
              index > 0 && <Divider />
            }
            <Stack>
              <Typography
                variant='subtitle2'
                sx={{
                  px: 2,
                  pt: 2,
                  fontWeight: 700,
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: section.color,
                }}
              >
                <Translate expression={section.title} />
              </Typography>
              {section.tasks.map(renderTask)}
            </Stack>
          </React.Fragment>
        ))
      }
      {
        (isEditorOpen && !visibleSections.some((section) => section.key === 'completed')) &&
          <Box sx={{mt: 1, px: 2}}>
            <TaskEditor
              task={{
                connectionInfo: [{
                  connectionRn: caregiverData.caregiverRn,
                  firstName: caregiverFirstName,
                  lastName: caregiverLastName,
                }],
              }}
              onCancel={closeEditor}
              onSubmit={edits.addTask}
            />
          </Box>
      }
      {
        (!isEditorOpen && !visibleSections.some((section) => section.key === 'completed')) &&
          <Box sx={{px: 2, py: 1}}>
            <Box
              component='button'
              onClick={addTask}
              sx={{
                background: 'none',
                border: 'none',
                color: 'primary.main',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '14px',
                p: 0,
              }}
            >
              <Box component='span'>+</Box>
              <Translate expression='tasks.Common.NewTask' />
            </Box>
          </Box>
      }
    </Stack>
  );
};

TaskList.propTypes = {
  caregiverData: PropTypes.object,
  caregiverTasks: PropTypes.array.isRequired,
  caregiverBackend: PropTypes.object.isRequired,
  playbooks: PropTypes.array.isRequired,
};

const mapStateToProps = (state) => ({
  playbooks: state.playbooks.allPlaybooks,
});

export default connect(mapStateToProps, dispatch)(React.memo(TaskList));
